'use client';
import React from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

export default function FeatureCards() {
  const { t } = useLanguage();
  const hp = t?.homePage;
  const fc = t?.featureCards;

  const features = [
    {
      id: 'feat-dna',
      emoji: '🧬',
      title: fc?.dnaTitle ?? 'Dream DNA Analysis',
      desc: fc?.dnaDesc ?? 'The proprietary methodology that analyzes the complete DNA of your dream — strengths, risks, opportunities, and full business potential.',
      color: 'text-primary',
      borderColor: 'border-primary/20',
      bgColor: 'bg-primary/8',
      tags: [fc?.dnaTag1 ?? 'Dream Score™', fc?.dnaTag2 ?? 'Business DNA', fc?.dnaTag3 ?? 'Opportunities'],
    },
    {
      id: 'feat-brand',
      emoji: '🎨',
      title: fc?.brandTitle ?? 'Brand Strategy',
      desc: fc?.brandDesc ?? 'Build your complete brand identity — name, colors, slogans, logo concepts, and brand personality — all from your dream.',
      color: 'text-accent',
      borderColor: 'border-accent/20',
      bgColor: 'bg-accent/8',
      tags: [fc?.brandTag1 ?? 'Brand Kit', fc?.brandTag2 ?? 'Identity', fc?.brandTag3 ?? 'Positioning'],
    },
    {
      id: 'feat-products',
      emoji: '📦',
      title: fc?.productsTitle ?? 'Product Strategy',
      desc: fc?.productsDesc ?? 'Define your product line, pricing strategy, packaging concepts, and revenue model with AI-powered clarity.',
      color: 'text-mint',
      borderColor: 'border-mint/20',
      bgColor: 'bg-mint/8',
      tags: [fc?.productsTag1 ?? 'Products', fc?.productsTag2 ?? 'Pricing', fc?.productsTag3 ?? 'Revenue'],
    },
    {
      id: 'feat-content',
      emoji: '📣',
      title: fc?.contentTitle ?? 'Content Plan',
      desc: fc?.contentDesc ?? 'Get a complete content strategy — post ideas, reel concepts, captions, and a 7-day content calendar ready to execute.',
      color: 'text-pink-400',
      borderColor: 'border-pink-400/20',
      bgColor: 'bg-pink-400/8',
      tags: [fc?.contentTag1 ?? 'Social Media', fc?.contentTag2 ?? 'Captions', fc?.contentTag3 ?? 'Calendar'],
    },
    {
      id: 'feat-launch',
      emoji: '🚀',
      title: fc?.launchTitle ?? 'Launch Roadmap',
      desc: fc?.launchDesc ?? 'A 90-day roadmap with weekly milestones, quick wins, and a clear path from idea to first sale.',
      color: 'text-orange-400',
      borderColor: 'border-orange-400/20',
      bgColor: 'bg-orange-400/8',
      tags: [fc?.launchTag1 ?? '90-Day Plan', fc?.launchTag2 ?? 'Milestones', fc?.launchTag3 ?? 'Quick Wins'],
    },
    {
      id: 'feat-visuals',
      emoji: '🖼️',
      title: fc?.visualsTitle ?? 'Visual Concepts',
      desc: fc?.visualsDesc ?? 'AI-generated visual inspiration for your brand, products, content, and marketing materials.',
      color: 'text-purple-400',
      borderColor: 'border-purple-400/20',
      bgColor: 'bg-purple-400/8',
      tags: [fc?.visualsTag1 ?? 'Images', fc?.visualsTag2 ?? 'Mockups', fc?.visualsTag3 ?? 'Brand Visuals'],
    },
    {
      id: 'feat-files',
      emoji: '📁',
      title: fc?.filesTitle ?? 'Project Files',
      desc: fc?.filesDesc ?? 'All generated assets, images, PDFs, and documents organized in your personal project workspace.',
      color: 'text-blue-400',
      borderColor: 'border-blue-400/20',
      bgColor: 'bg-blue-400/8',
      tags: [fc?.filesTag1 ?? 'Assets', fc?.filesTag2 ?? 'PDFs', fc?.filesTag3 ?? 'Workspace'],
    },
    {
      id: 'feat-progress',
      emoji: '📊',
      title: fc?.progressTitle ?? 'Progress Tracking',
      desc: fc?.progressDesc ?? 'Task checklists, milestone tracking, and project completion status — stay on track from dream to launch.',
      color: 'text-green-400',
      borderColor: 'border-green-400/20',
      bgColor: 'bg-green-400/8',
      tags: [fc?.progressTag1 ?? 'Tasks', fc?.progressTag2 ?? 'Milestones', fc?.progressTag3 ?? 'Progress'],
    },
  ];

  return (
    <section className="py-24 bg-background">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="text-center mb-14">
          <p className="text-xs font-600 uppercase tracking-widest text-primary mb-3">
            {hp?.featuresLabel ?? 'Dream DNA Studio™'}
          </p>
          <h2 className="text-hero-md font-700 text-foreground mb-4">
            {hp?.featuresTitle ?? 'Everything Built Inside Your Dream DNA™'}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base">
            {hp?.featuresSubtitle ?? 'Dream DNA Studio™ doesn\'t just generate a plan — it builds your complete dream-to-reality system.'}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-5">
          {features?.map((feat) => (
            <div
              key={feat?.id}
              className="glass-card feature-card-premium rounded-2xl p-6 flex flex-col border border-border"
            >
              <div className={`w-12 h-12 rounded-xl ${feat?.bgColor} flex items-center justify-center text-2xl mb-4 border ${feat?.borderColor} transition-transform duration-300 group-hover:scale-110`}>
                {feat?.emoji}
              </div>
              <h3 className={`text-base font-700 ${feat?.color} mb-2`}>{feat?.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1">
                {feat?.desc}
              </p>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {feat?.tags?.map((tag, tagIdx) => (
                  <span
                    key={`tag-${feat?.id}-${tagIdx}`}
                    className="text-[10px] font-600 px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <Link
                href="/dream-workspace"
                className={`text-xs font-600 ${feat?.color} flex items-center gap-1 hover:gap-2 transition-all`}
              >
                {hp?.exploreTool ?? 'Explore'} <ArrowRight size={12} />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}